import {useMemo, useRef, useEffect} from 'react';
import {useRegions} from '../../hooks';
import {RegionModel, RegionWithMetaModel} from '../../model';
import {Select} from '@gravity-ui/uikit';
import styles from './SelectRegions.module.scss';

interface SelectRegionsProps {
  value: string[];
  onUpdate: (value: string[]) => void | ((values: string[], names: Record<string, string>) => void);
  name?: string;
  size?: 's' | 'm' | 'l';
  placeholder?: string;
  error?: string | false;
  multiple?: boolean;
  isSaveSessionStorage?: boolean;
}

export const SelectRegions = ({
  value,
  onUpdate,
  name,
  size = 'm',
  placeholder = 'Регион',
  error,
  multiple = false,
  isSaveSessionStorage = false,
}: SelectRegionsProps) => {
  const {getInfiniteRegions} = useRegions({pageSize: 200});
  const regionSelectRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLButtonElement>(null);
  const {
    data: infiniteRegionsData,
    isLoading: isRegionsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = getInfiniteRegions;
  const regionOptions = useMemo(() => {
    return (
      infiniteRegionsData?.pages.flatMap((page: RegionWithMetaModel) =>
        page.items.map((region: RegionModel) => ({
          value: region.id,
          content: region.name,
        })),
      ) || []
    );
  }, [infiniteRegionsData]);

  // Автоматически загружаем все страницы при наличии следующей страницы
  useEffect(() => {
    if (hasNextPage && !isRegionsLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isRegionsLoading, isFetchingNextPage, fetchNextPage]);

  // Обработчик открытия выпадающего списка
  const handleRegionSelectOpen = (open: boolean) => {
    if (open) {
      // Добавляем обработчик скролла при открытии селекта
      setTimeout(() => {
        const popupElement = document.querySelector('.g-select-popup__content');
        if (popupElement) {
          regionSelectRef.current = popupElement as HTMLDivElement;
          popupElement.addEventListener('scroll', handleRegionSelectScroll);
        }
      }, 100);
    } else {
      // Удаляем обработчик при закрытии
      if (regionSelectRef.current) {
        regionSelectRef.current.removeEventListener('scroll', handleRegionSelectScroll);
        regionSelectRef.current = null;
      }
    }
  };

  // Обработчик скролла в выпадающем списке
  const handleRegionSelectScroll = () => {
    if (!regionSelectRef.current) return;

    const {scrollTop, scrollHeight, clientHeight} = regionSelectRef.current;
    // Если пользователь прокрутил до конца списка и есть ещё данные
    if (scrollTop + clientHeight >= scrollHeight - 20 && hasNextPage && !isRegionsLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Обработчик обновления с сохранением в sessionStorage
  const handleUpdate = (newValue: string[]) => {
    // Вызываем обычное обновление
    onUpdate(newValue);

    // Если включено сохранение в sessionStorage
    if (isSaveSessionStorage) {
      // Находим объекты выбранных категорий с id и именем
      const selectedRegions = newValue
        .map((id) => {
          const option = regionOptions.find((opt) => opt.value === id);
          return option ? {id: option.value, name: option.content} : null;
        })
        .filter(Boolean);

      // Сохраняем в sessionStorage
      sessionStorage.setItem(`regions-${name || 'default'}`, JSON.stringify(selectedRegions));
    }
  };

  return (
    <Select
      className={styles.select}
      size={size}
      ref={selectRef}
      name={name}
      options={regionOptions}
      onUpdate={handleUpdate}
      value={value}
      onOpenChange={handleRegionSelectOpen}
      loading={isRegionsLoading}
      placeholder={placeholder}
      errorPlacement="inside"
      errorMessage={error}
      validationState={error ? 'invalid' : undefined}
      filterable
      filterPlaceholder="Поиск"
      multiple={multiple}
    />
  );
};
